import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store from "../Store";
import Datastore from "../../communication/Datastore";
import Approval from "../../types/admin/Approval";
import { Guid } from "guid-typescript";
import { unwrappedDispatch } from "../../foundation/Dispatching";

// Define the type for our state store entry
type ApprovalsState = {
    approvals: Approval[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: ApprovalsState = {
    approvals: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const approvalsSlice = createSlice({
    name: "approvals",
    initialState: initialState,
    reducers: {
        resetApprovalsStatus(state) {
            state.status = "idle";
        },
        removeApprovals: (state, action) => {
            state.approvals = state.approvals.filter(
                item => !action.payload.some(id => id === item.id),
            );
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    approvals:
                        rawData?.payload?.approvals?.approvals?.map(
                            c => new Approval(c),
                        ) || [],
                    status: !rawData?.payload?.approvals?.approvals?.length
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchApprovals.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchApprovals.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.approvals = action.payload.map(c => new Approval(c));
            })
            .addCase(fetchApprovals.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(approveApprovals.fulfilled, (state, action) => {
                state.status = "succeeded";
                const updatedApprovals = action.payload.map(
                    c => new Approval(c),
                );
                state.approvals = state.approvals
                    .filter(
                        tt =>
                            !updatedApprovals.some(
                                updated => updated.id === tt.id,
                            ),
                    )
                    .concat(updatedApprovals);
            })
            .addCase(rejectApprovals.fulfilled, (state, action) => {
                state.status = "succeeded";
                const updatedApprovals = action.payload.map(
                    c => new Approval(c),
                );
                state.approvals = state.approvals
                    .filter(
                        tt =>
                            !updatedApprovals.some(
                                updated => updated.id === tt.id,
                            ),
                    )
                    .concat(updatedApprovals);
            });
    },
});

const { resetApprovalsStatus, removeApprovals } = approvalsSlice.actions;

// This is a thunk, they are used to load the state
export const fetchApprovals = createAsyncThunk(
    "approvals/fetchApprovals",
    () => Datastore.getType<Approval[]>("api/Approvals"),
    {
        condition: () => {
            const { approvals } = store.getState().db;
            return approvals.status === "idle";
        },
    },
);
export const initialiseApprovalsDetails = async (): Promise<void> => {
    await unwrappedDispatch(fetchApprovals());
};
export const reinitialiseApprovalsDetails = async (): Promise<void> => {
    store.dispatch(resetApprovalsStatus());
    await unwrappedDispatch(fetchApprovals());
};
export const clearApprovalsDetails = () => {
    unwrappedDispatch(resetApprovalsStatus());
};
export const addApprovals = createAsyncThunk(
    "approvals/addApprovals",
    async (approvals: Approval[], thunkAPI): Promise<Approval[]> =>
        await Datastore.postType<Approval[]>("api/Approvals", approvals),
);
export const approveApprovals = createAsyncThunk(
    "approvals/approveApprovals",
    async (approval: Approval, thunkAPI): Promise<Approval[]> =>
        await Datastore.putType<Approval[]>(`api/Approvals/Approve?approvalId=${approval.id}`),
);
export const rejectApprovals = createAsyncThunk(
    "approvals/rejectApprovals",
    async (approval: Approval, thunkAPI): Promise<Approval[]> =>
        await Datastore.putType<Approval[]>(
            `api/Approvals/Reject?approvalId=${approval.id}`,
            approval.rejectionReason,
        ),
);

// We have to export the reducer as this is added to the state store
export default approvalsSlice.reducer;
